// src/components/roleplay/PersonaAdmin.js
// Admin panel: full CRUD over roleplay personas, plus a compact editor for
// the three difficulty-behavior prompts. Self-contained — no outer page
// chrome; the parent (RoleplayApp) supplies the tab bar and container.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { TRACKS, DIFFICULTIES, DEFAULT_DIFFICULTY_CONFIG, MODELS, DEFAULT_MODEL, DEFAULT_LIMITS, slugifyPersonaId, trackLabel } from '../../config/roleplay';
import { subscribePersonas, savePersona, archivePersona, deletePersona, subscribeConfig, saveConfigDoc, seedRoleplay } from '../../data/roleplay';

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const SectionSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0 0 16px 0;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Button = styled.button`
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid #0e8c6a;
  background: #0e8c6a;
  color: #fff;
  cursor: pointer;

  &:hover {
    background: #0b7057;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: #ffffff;
  color: #0e8c6a;

  &:hover {
    background: #f0fdf9;
  }
`;

const TableWrap = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 32px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 16px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e2e8f0;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  color: #1e293b;
  vertical-align: middle;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #0e8c6a;
  background: #e6f5f0;
`;

const ArchivedBadge = styled(Badge)`
  color: #94a3b8;
  background: #f1f5f9;
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 600;
  color: #0e8c6a;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeleteLinkButton = styled(LinkButton)`
  color: #dc2626;
`;

const ConfirmText = styled.span`
  font-size: 13px;
  color: #64748b;
`;

const Muted = styled.span`
  color: #94a3b8;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

const FormPanel = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
`;

const FormTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 18px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldFull = styled(Field)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const inputStyles = `
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0e8c6a;
    box-shadow: 0 0 0 3px rgba(14, 140, 106, 0.12);
  }
`;

const Input = styled.input`
  ${inputStyles}
`;

const Select = styled.select`
  ${inputStyles}
`;

const TextArea = styled.textarea`
  ${inputStyles}
  min-height: 110px;
  resize: vertical;
  line-height: 1.5;
`;

const SmallTextArea = styled(TextArea)`
  min-height: 80px;
`;

const HelpText = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 4px 0 0 0;
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled(ErrorMessage)`
  background: #f0fdf9;
  border: 1px solid #bbf7e0;
  color: #0e8c6a;
`;

// ── Known-objections list editor ────────────────────────────────────────
const ObjectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ObjectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: #94a3b8;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  flex-shrink: 0;

  &:hover {
    color: #dc2626;
    background: #fef2f2;
  }
`;

const AddObjectionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

// ── Difficulty overrides (per-persona, optional) ────────────────────────
const OverrideGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const OverrideCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OverrideLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #475569;
`;

// ── Difficulty behavior config editor ───────────────────────────────────
const DiffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const emptyForm = {
  title: '',
  company: '',
  track: TRACKS[0]?.key || '',
  brief: '',
  knownObjections: [],
  difficultyOverrides: {},
  archived: false,
};

function personaToForm(p) {
  return {
    title: p.title || '',
    company: p.company || '',
    track: p.track || TRACKS[0]?.key || '',
    brief: p.brief || '',
    knownObjections: Array.isArray(p.knownObjections) ? p.knownObjections : [],
    difficultyOverrides: p.difficultyOverrides || {},
    archived: !!p.archived,
  };
}

function PersonaAdmin({ user }) {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [newObjection, setNewObjection] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState('');

  const [confirmArchiveId, setConfirmArchiveId] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Difficulty behavior config (separate small form below the persona list)
  const [diffConfig, setDiffConfig] = useState(null); // null until loaded
  const [diffForm, setDiffForm] = useState({ cooperative: '', standard: '', hostile: '' });
  const [savingDiff, setSavingDiff] = useState(false);
  const [diffMsg, setDiffMsg] = useState('');

  // Model config (which Claude model powers replies + scoring)
  const [modelConfigId, setModelConfigId] = useState(null); // null until loaded
  const [modelId, setModelId] = useState(DEFAULT_MODEL);
  const [modelTouched, setModelTouched] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [modelMsg, setModelMsg] = useState('');

  // Cost guardrails / limits config
  const [limitsConfig, setLimitsConfig] = useState(null); // null until loaded
  const [limitsForm, setLimitsForm] = useState(DEFAULT_LIMITS);
  const [limitsTouched, setLimitsTouched] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsMsg, setLimitsMsg] = useState('');

  useEffect(() => {
    const unsub = subscribePersonas(
      (rows) => {
        setPersonas(rows);
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        setLoadError(
          err?.code === 'permission-denied'
            ? "You don't have permission to view personas. Contact an admin if this seems wrong."
            : 'Could not load personas. Please refresh and try again.'
        );
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribeConfig(
      (cfg) => {
        setDiffConfig(cfg?.difficulty || {});
        setModelConfigId(cfg?.model?.id || null);
        setLimitsConfig(cfg?.limits || null);
      },
      () => {
        setDiffConfig({});
        setModelConfigId(null);
        setLimitsConfig(null);
      }
    );
    return () => unsub();
  }, []);

  // Populate the model selector from config (fallback to default), until the
  // admin starts changing it.
  useEffect(() => {
    if (modelTouched) return;
    setModelId(modelConfigId || DEFAULT_MODEL);
  }, [modelConfigId, modelTouched]);

  // Populate the limits form from config (fallback to defaults), until edited.
  useEffect(() => {
    if (limitsTouched) return;
    setLimitsForm({ ...DEFAULT_LIMITS, ...(limitsConfig || {}) });
  }, [limitsConfig, limitsTouched]);

  // Populate the difficulty form whenever config arrives (falling back to defaults),
  // but only before the admin has started editing (avoid clobbering in-progress edits).
  const [diffTouched, setDiffTouched] = useState(false);
  useEffect(() => {
    if (diffConfig == null || diffTouched) return;
    setDiffForm({
      cooperative: diffConfig.cooperative?.behavior ?? DEFAULT_DIFFICULTY_CONFIG.cooperative.behavior,
      standard: diffConfig.standard?.behavior ?? DEFAULT_DIFFICULTY_CONFIG.standard.behavior,
      hostile: diffConfig.hostile?.behavior ?? DEFAULT_DIFFICULTY_CONFIG.hostile.behavior,
    });
  }, [diffConfig, diffTouched]);

  const sortedPersonas = useMemo(() => {
    return [...personas].sort((a, b) => {
      if (!!a.archived !== !!b.archived) return a.archived ? 1 : -1;
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [personas]);

  const existingIds = useMemo(() => new Set(personas.map((p) => p.id)), [personas]);

  function openCreateForm() {
    setEditingId(null);
    setForm(emptyForm);
    setNewObjection('');
    setError('');
    setShowForm(true);
  }

  function openEditForm(persona) {
    setEditingId(persona.id);
    setForm(personaToForm(persona));
    setNewObjection('');
    setError('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setNewObjection('');
    setError('');
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addObjection() {
    const text = newObjection.trim();
    if (!text) return;
    setForm((prev) => ({ ...prev, knownObjections: [...prev.knownObjections, text] }));
    setNewObjection('');
  }

  function removeObjection(idx) {
    setForm((prev) => ({
      ...prev,
      knownObjections: prev.knownObjections.filter((_, i) => i !== idx),
    }));
  }

  function updateOverrideBehavior(diffKey, behavior) {
    setForm((prev) => ({
      ...prev,
      difficultyOverrides: {
        ...prev.difficultyOverrides,
        [diffKey]: behavior.trim()
          ? { ...(prev.difficultyOverrides[diffKey] || {}), behavior }
          : undefined,
      },
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!TRACKS.some((t) => t.key === form.track)) {
      setError('Pick a valid track.');
      return;
    }

    let id = editingId;
    if (!id) {
      id = slugifyPersonaId(form.title);
      if (!id) {
        setError('Could not derive an id from this title — use at least one letter or number.');
        return;
      }
      if (existingIds.has(id)) {
        setError(`A persona with id "${id}" already exists. Use a more specific title.`);
        return;
      }
    }

    // Strip out any undefined override entries (behavior cleared to blank).
    const cleanOverrides = {};
    for (const [k, v] of Object.entries(form.difficultyOverrides || {})) {
      if (v && v.behavior && v.behavior.trim()) cleanOverrides[k] = v;
    }

    const payload = {
      title: form.title.trim(),
      company: form.company.trim(),
      track: form.track,
      brief: form.brief.trim(),
      knownObjections: form.knownObjections,
      difficultyOverrides: cleanOverrides,
      archived: !!form.archived,
    };

    setSaving(true);
    try {
      await savePersona(id, payload, user?.email);
      closeForm();
    } catch (err) {
      setError(err?.message || 'Failed to save persona.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSeed() {
    setSeedError('');
    setSeeding(true);
    try {
      await seedRoleplay(user?.email);
      // The persona list + difficulty config refresh live via their listeners.
    } catch (err) {
      setSeedError(err?.message || 'Failed to seed starter data.');
    } finally {
      setSeeding(false);
    }
  }

  async function handleArchive(id) {
    setArchivingId(id);
    try {
      await archivePersona(id);
      setConfirmArchiveId(null);
    } catch (err) {
      setError(err?.message || 'Failed to archive persona.');
    } finally {
      setArchivingId(null);
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      await deletePersona(id);
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete persona.');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSaveLimits(e) {
    e.preventDefault();
    setLimitsMsg('');
    setSavingLimits(true);
    try {
      const num = (v, d) => (Number(v) > 0 ? Math.floor(Number(v)) : d);
      await saveConfigDoc('limits', {
        enabled: !!limitsForm.enabled,
        maxTurnsPerCall: num(limitsForm.maxTurnsPerCall, DEFAULT_LIMITS.maxTurnsPerCall),
        dailyTurnsPerRep: num(limitsForm.dailyTurnsPerRep, DEFAULT_LIMITS.dailyTurnsPerRep),
        maxMessageChars: num(limitsForm.maxMessageChars, DEFAULT_LIMITS.maxMessageChars),
      });
      setLimitsMsg('Limits saved.');
      setLimitsTouched(false);
    } catch (err) {
      setLimitsMsg(err?.message || 'Failed to save limits.');
    } finally {
      setSavingLimits(false);
    }
  }

  async function handleSaveModel(e) {
    e.preventDefault();
    setModelMsg('');
    setSavingModel(true);
    try {
      await saveConfigDoc('model', { id: modelId, updatedBy: (user?.email || '').toLowerCase() });
      setModelMsg('Model saved.');
      setModelTouched(false);
    } catch (err) {
      setModelMsg(err?.message || 'Failed to save model.');
    } finally {
      setSavingModel(false);
    }
  }

  async function handleSaveDifficultyConfig(e) {
    e.preventDefault();
    setDiffMsg('');
    setSavingDiff(true);
    try {
      await saveConfigDoc('difficulty', {
        cooperative: { label: DIFFICULTIES.find((d) => d.key === 'cooperative')?.label || 'Cooperative', behavior: diffForm.cooperative },
        standard: { label: DIFFICULTIES.find((d) => d.key === 'standard')?.label || 'Standard', behavior: diffForm.standard },
        hostile: { label: DIFFICULTIES.find((d) => d.key === 'hostile')?.label || 'Hostile', behavior: diffForm.hostile },
      });
      setDiffMsg('Difficulty behavior saved.');
      setDiffTouched(false);
    } catch (err) {
      setDiffMsg(err?.message || 'Failed to save difficulty config.');
    } finally {
      setSavingDiff(false);
    }
  }

  return (
    <div>
      <SectionTitle>Model</SectionTitle>
      <SectionSubtitle>
        Which Claude model powers roleplay replies, scoring, and objection mining. Applies to
        everyone; changes take effect on the next call.
      </SectionSubtitle>

      <FormPanel>
        {modelMsg &&
          (modelMsg.toLowerCase().includes('fail') ? (
            <ErrorMessage>{modelMsg}</ErrorMessage>
          ) : (
            <SuccessMessage>{modelMsg}</SuccessMessage>
          ))}
        <form onSubmit={handleSaveModel}>
          <Field style={{ maxWidth: 460 }}>
            <Label htmlFor="model-select">Claude model</Label>
            <Select
              id="model-select"
              value={modelId}
              onChange={(e) => {
                setModelTouched(true);
                setModelId(e.target.value);
              }}
            >
              {MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
              {modelId && !MODELS.some((m) => m.id === modelId) && (
                <option value={modelId}>{modelId} (custom)</option>
              )}
            </Select>
            <HelpText>{MODELS.find((m) => m.id === modelId)?.note || 'Custom model id.'}</HelpText>
          </Field>
          <FormActions>
            <Button type="submit" disabled={savingModel || !modelId}>
              {savingModel ? 'Saving...' : 'Save model'}
            </Button>
          </FormActions>
        </form>
      </FormPanel>

      <SectionTitle>Cost guardrails</SectionTitle>
      <SectionSubtitle>
        Hard limits enforced on the server so the LLM can’t be run blindly or overshoot on cost.
      </SectionSubtitle>

      <FormPanel>
        {limitsMsg &&
          (limitsMsg.toLowerCase().includes('fail') ? (
            <ErrorMessage>{limitsMsg}</ErrorMessage>
          ) : (
            <SuccessMessage>{limitsMsg}</SuccessMessage>
          ))}
        <form onSubmit={handleSaveLimits}>
          <Field style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1e293b', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={!!limitsForm.enabled}
                onChange={(e) => {
                  setLimitsTouched(true);
                  setLimitsForm((p) => ({ ...p, enabled: e.target.checked }));
                }}
              />
              Roleplay enabled
            </label>
            <HelpText>Uncheck to immediately disable all live calls (kill switch).</HelpText>
          </Field>
          <FormGrid>
            <Field>
              <Label htmlFor="lim-turns">Max turns per call</Label>
              <Input
                id="lim-turns"
                type="number"
                min="1"
                value={limitsForm.maxTurnsPerCall}
                onChange={(e) => {
                  setLimitsTouched(true);
                  setLimitsForm((p) => ({ ...p, maxTurnsPerCall: e.target.value }));
                }}
              />
            </Field>
            <Field>
              <Label htmlFor="lim-daily">Daily turns per rep</Label>
              <Input
                id="lim-daily"
                type="number"
                min="1"
                value={limitsForm.dailyTurnsPerRep}
                onChange={(e) => {
                  setLimitsTouched(true);
                  setLimitsForm((p) => ({ ...p, dailyTurnsPerRep: e.target.value }));
                }}
              />
            </Field>
            <Field>
              <Label htmlFor="lim-chars">Max message length (chars)</Label>
              <Input
                id="lim-chars"
                type="number"
                min="1"
                value={limitsForm.maxMessageChars}
                onChange={(e) => {
                  setLimitsTouched(true);
                  setLimitsForm((p) => ({ ...p, maxMessageChars: e.target.value }));
                }}
              />
            </Field>
          </FormGrid>
          <FormActions>
            <Button type="submit" disabled={savingLimits}>
              {savingLimits ? 'Saving...' : 'Save limits'}
            </Button>
          </FormActions>
        </form>
      </FormPanel>

      <SectionTitle>Personas</SectionTitle>
      <SectionSubtitle>
        Manage the prospects reps roleplay against — title, brief, known objections, and
        optional per-difficulty behavior tweaks.
      </SectionSubtitle>

      {error && !showForm && <ErrorMessage>{error}</ErrorMessage>}
      {seedError && <ErrorMessage>{seedError}</ErrorMessage>}

      <TopBar>
        <div />
        {!showForm && (
          <RowActions>
            {!loading && !loadError && personas.length === 0 && (
              <SecondaryButton onClick={handleSeed} disabled={seeding}>
                {seeding ? 'Seeding…' : 'Seed starter data'}
              </SecondaryButton>
            )}
            <Button onClick={openCreateForm}>+ New persona</Button>
          </RowActions>
        )}
      </TopBar>

      {showForm && (
        <FormPanel>
          <FormTitle>{editingId ? `Edit persona — ${editingId}` : 'New persona'}</FormTitle>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <Field>
                <Label htmlFor="p-title">Title</Label>
                <Input
                  id="p-title"
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g. Operations Director"
                />
                {!editingId && form.title.trim() && (
                  <HelpText>
                    Id: <strong>{slugifyPersonaId(form.title) || '—'}</strong>
                    {existingIds.has(slugifyPersonaId(form.title)) && (
                      <span style={{ color: '#dc2626' }}> — already in use</span>
                    )}
                  </HelpText>
                )}
              </Field>

              <Field>
                <Label htmlFor="p-company">Company</Label>
                <Input
                  id="p-company"
                  type="text"
                  value={form.company}
                  onChange={(e) => updateField('company', e.target.value)}
                  placeholder="e.g. A mid-size firm"
                />
              </Field>

              <Field>
                <Label htmlFor="p-track">Track</Label>
                <Select id="p-track" value={form.track} onChange={(e) => updateField('track', e.target.value)}>
                  {TRACKS.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <FieldFull>
                <Label htmlFor="p-brief">Brief</Label>
                <TextArea
                  id="p-brief"
                  value={form.brief}
                  onChange={(e) => updateField('brief', e.target.value)}
                  placeholder="Who this persona is, what they care about, how they screen cold calls..."
                />
              </FieldFull>

              <FieldFull>
                <Label>Known objections</Label>
                <ObjectionList>
                  {form.knownObjections.map((obj, idx) => (
                    <ObjectionRow key={idx}>
                      <Input type="text" value={obj} readOnly style={{ flex: 1 }} />
                      <RemoveBtn type="button" onClick={() => removeObjection(idx)} aria-label="Remove">
                        ×
                      </RemoveBtn>
                    </ObjectionRow>
                  ))}
                  <AddObjectionRow>
                    <Input
                      type="text"
                      value={newObjection}
                      onChange={(e) => setNewObjection(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addObjection();
                        }
                      }}
                      placeholder="Add an objection line..."
                      style={{ flex: 1 }}
                    />
                    <SecondaryButton type="button" onClick={addObjection}>
                      Add
                    </SecondaryButton>
                  </AddObjectionRow>
                </ObjectionList>
              </FieldFull>

              <FieldFull>
                <Label>Difficulty behavior overrides — optional</Label>
                <HelpText style={{ marginBottom: 6 }}>
                  Leave blank to fall back to the global behavior for that difficulty.
                </HelpText>
                <OverrideGrid>
                  {DIFFICULTIES.map((d) => (
                    <OverrideCard key={d.key}>
                      <OverrideLabel>{d.label}</OverrideLabel>
                      <SmallTextArea
                        value={form.difficultyOverrides[d.key]?.behavior || ''}
                        onChange={(e) => updateOverrideBehavior(d.key, e.target.value)}
                        placeholder={`Optional override behavior for ${d.label}...`}
                      />
                    </OverrideCard>
                  ))}
                </OverrideGrid>
              </FieldFull>
            </FormGrid>

            <FormActions>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create persona'}
              </Button>
              <SecondaryButton type="button" onClick={closeForm} disabled={saving}>
                Cancel
              </SecondaryButton>
            </FormActions>
          </form>
        </FormPanel>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Title</Th>
              <Th>Company</Th>
              <Th>Track</Th>
              <Th>Objections</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {sortedPersonas.map((p) => (
              <tr key={p.id}>
                <Td>{p.title || <Muted>Untitled</Muted>}</Td>
                <Td>{p.company || <Muted>—</Muted>}</Td>
                <Td>{trackLabel(p.track)}</Td>
                <Td>{Array.isArray(p.knownObjections) ? p.knownObjections.length : 0}</Td>
                <Td>{p.archived ? <ArchivedBadge>Archived</ArchivedBadge> : <Badge>Active</Badge>}</Td>
                <Td>
                  {confirmDeleteId === p.id ? (
                    <RowActions>
                      <ConfirmText>Permanently delete?</ConfirmText>
                      <DeleteLinkButton onClick={() => handleDelete(p.id)} disabled={deletingId === p.id}>
                        {deletingId === p.id ? 'Deleting...' : 'Confirm delete'}
                      </DeleteLinkButton>
                      <LinkButton onClick={() => setConfirmDeleteId(null)}>Cancel</LinkButton>
                    </RowActions>
                  ) : confirmArchiveId === p.id ? (
                    <RowActions>
                      <ConfirmText>Archive this persona?</ConfirmText>
                      <LinkButton onClick={() => handleArchive(p.id)} disabled={archivingId === p.id}>
                        {archivingId === p.id ? 'Archiving...' : 'Confirm'}
                      </LinkButton>
                      <LinkButton onClick={() => setConfirmArchiveId(null)}>Cancel</LinkButton>
                    </RowActions>
                  ) : (
                    <RowActions>
                      <LinkButton onClick={() => openEditForm(p)}>Edit</LinkButton>
                      {!p.archived && (
                        <LinkButton onClick={() => setConfirmArchiveId(p.id)}>Archive</LinkButton>
                      )}
                      <DeleteLinkButton onClick={() => setConfirmDeleteId(p.id)}>Delete</DeleteLinkButton>
                    </RowActions>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {!loading && !loadError && personas.length === 0 && (
          <EmptyState>
            No personas yet. Click <strong>Seed starter data</strong> above to load two
            example prospects and the default difficulty settings, or create your own.
          </EmptyState>
        )}
        {loading && <EmptyState>Loading personas...</EmptyState>}
        {!loading && loadError && <EmptyState>{loadError}</EmptyState>}
      </TableWrap>

      <SectionTitle>Difficulty behavior</SectionTitle>
      <SectionSubtitle>
        Tune how the prospect behaves at each difficulty tier globally. Personas can override
        these individually above.
      </SectionSubtitle>

      <FormPanel>
        {diffMsg && (
          diffMsg.toLowerCase().includes('fail') ? (
            <ErrorMessage>{diffMsg}</ErrorMessage>
          ) : (
            <SuccessMessage>{diffMsg}</SuccessMessage>
          )
        )}
        <form onSubmit={handleSaveDifficultyConfig}>
          <DiffGrid>
            {DIFFICULTIES.map((d) => (
              <Field key={d.key}>
                <Label htmlFor={`diff-${d.key}`}>{d.label}</Label>
                <TextArea
                  id={`diff-${d.key}`}
                  value={diffForm[d.key] || ''}
                  onChange={(e) => {
                    setDiffTouched(true);
                    setDiffForm((prev) => ({ ...prev, [d.key]: e.target.value }));
                  }}
                  style={{ minHeight: 180 }}
                />
              </Field>
            ))}
          </DiffGrid>
          <FormActions>
            <Button type="submit" disabled={savingDiff}>
              {savingDiff ? 'Saving...' : 'Save difficulty behavior'}
            </Button>
          </FormActions>
        </form>
      </FormPanel>
    </div>
  );
}

export default PersonaAdmin;
